/**
 * `/integrations` — the accounts Wend uses on a tenant's behalf.
 *
 * This file is a shell. It says one sentence, names the channels, and hands the
 * rail a panel for each; it renders no credential, no warning and no switch of
 * its own. The rail and its URL live in `integration-tabs.tsx`, the Email
 * channel in `EmailTab.tsx`, the WhatsApp channel in `WhatsAppCard.tsx`.
 *
 * ## What it used to be, and why that stopped working
 *
 * The superseded position, kept because it was right at the time and the next
 * reader is owed the reason it changed: slice 3 put Amazon SES on this page and
 * a WhatsApp card beside it saying plainly that WhatsApp was not built, because
 * a page called Integrations showing only SES would misdescribe the shape of
 * the product. Slice 7 built the WhatsApp half. Slice 11 added a third card —
 * tracking, which is not an account but a decision about the account already
 * connected — and argued, correctly, that it belonged directly under SES and
 * above WhatsApp because it is a property of the SES connection.
 *
 * Every one of those arguments was about *order*, and order was the only tool
 * the page had. The result was that connecting WhatsApp began by scrolling past
 * every SES configuration panel and the whole of the tracking card, and this
 * file alone had reached 1,012 lines. The page grew by accretion rather than by
 * design, and each slice appending one more card at the bottom is what
 * accretion looks like from the inside.
 *
 * ## The rail, and what it is for next
 *
 * A channel is a tab. That is not decoration: it separates the two jobs this
 * page runs together, which have completely different frequencies — connecting
 * an account happens once and then essentially never, while configuring one is
 * ongoing and expert. Each tab puts the first above the fold and the second
 * behind a collapsed **Advanced** disclosure. That disclosure is one component
 * on both tabs — `AdvancedDisclosure` — and `WhatsAppAdvanced` is what the
 * WhatsApp tab puts inside it. It was briefly two hand-rolled `<details>`,
 * because the halves of slice 16 were written in parallel; both headers keep
 * that as the superseded position, and the reason it did not stay that way is
 * that two disclosures which look identical and can drift apart on one page is
 * the accretion this slice exists to undo.
 *
 * **The rail is also the extension point.** Flow will bring sources — Shopify,
 * Amazon, Meesho — and each becomes an entry in `INTEGRATION_TAB_VALUES`, a
 * word and a glyph beside it, and one more line in the `panels` record below.
 * Not a fourth, fifth and sixth card stacked under WhatsApp, and not a second
 * page: the record is total over the union, so the compiler asks for the panel
 * as soon as the tab exists.
 *
 * ## The status on a tab, and the one thing this file translates
 *
 * Tabs hide things, and somebody on the Email tab has to be able to see that
 * WhatsApp needs attention without switching to it. Each panel reports the
 * verdict it is already rendering, so the rail cannot word an account
 * differently from the card inside it — `integration-tabs.tsx` argues that at
 * length, and the alternative it rejects is the rail deriving its own.
 *
 * The Email panel reports for itself. The WhatsApp panel reports through an
 * `onStatusChange` prop and owns its own vocabulary, so `WhatsAppStatusPanel`
 * below is the adapter between the two — and it is a translation, not a second
 * opinion: the label is the panel's own word for the state and the tone comes
 * from `whatsapp-reading.ts`'s own exported map.
 *
 * The rail passes `keepMounted`, so both panels load with the page whichever tab
 * is selected and each reports the verdict it is already rendering. That is why
 * this shell makes no request of its own: a third `GET /integrations` here would
 * be a second opinion about a connection the panel below is already describing,
 * and the two would disagree the moment somebody replaced a credential.
 */
import { AppLayout } from '../../components/AppLayout';
import { EmailTab } from './EmailTab';
import { MetaAdsTabPanel } from './MetaAdsCard';
import { WhatsAppTabPanel } from './WhatsAppCard';
import { IntegrationTabRail, useIntegrationTabStatusReporter } from './integration-tabs';
import { META_ADS_ATTENTION_TONE } from './meta-ads-reading';
import { WHATSAPP_TAB_ATTENTION_TONE } from './whatsapp-reading';

export function IntegrationsPage() {
  return (
    <AppLayout title="Integrations">
      <div className="flex flex-col gap-6">
        {/*
          One line, because the detail belongs where it is acted on: the connect
          form states in full what handing over a tenant's own AWS credentials
          means for their quota, their bill and their reputation, and it states
          it at the moment somebody is creating the key. Since slice 65 the line
          names both halves the rail holds accounts for — the messages Connect
          sends and the ads Reach publishes — because "sends" alone described
          two tabs of three.
        */}
        <p className="max-w-prose text-[0.92rem] leading-relaxed text-muted">
          Wend sends messages and publishes ads through your own accounts, not its own — so the
          sending reputation, the limits, the ad spend and the bill are yours.
        </p>

        <IntegrationTabRail
          panels={{
            email: <EmailTab />,
            whatsapp: <WhatsAppStatusPanel />,
            'meta-ads': <MetaAdsStatusPanel />,
          }}
        />
      </div>
    </AppLayout>
  );
}

/**
 * The WhatsApp panel, wired to the rail.
 *
 * A component rather than an inline callback because the reporter comes from
 * the rail's own context, and context is read where a node is rendered rather
 * than where it is created — the panels above are built outside the provider
 * and rendered inside it.
 */
function WhatsAppStatusPanel() {
  const report = useIntegrationTabStatusReporter();

  return (
    <WhatsAppTabPanel
      onStatusChange={(status) => {
        report('whatsapp', {
          label: status.label,
          tone: WHATSAPP_TAB_ATTENTION_TONE[status.attention],
        });
      }}
    />
  );
}

/**
 * The Meta ads panel, wired to the rail. See {@link WhatsAppStatusPanel} for
 * why this is a component rather than an inline callback.
 */
function MetaAdsStatusPanel() {
  const report = useIntegrationTabStatusReporter();

  return (
    <MetaAdsTabPanel
      onStatusChange={(status) => {
        report('meta-ads', {
          label: status.label,
          tone: META_ADS_ATTENTION_TONE[status.attention],
        });
      }}
    />
  );
}
